/**
 * **C07 · The raffle and bingo statement covers** (Stream C, Documents.dc.html): two half-letter
 * covers to the Charitable Gaming Division, on top of the state's own forms. The figures are the
 * forms' own lines from Money's state filings (`state_filings`, the one engine — BSL-CG-1619 for
 * the raffle, BSL-CG-142 for bingo), named by line; the tickets are the raffle books' (Marketing).
 * CIDER supplies the figures; the form is the state's.
 */
import { NO_DATA, type PaperCell, type PaperPage } from './paper_page.js';

/** A state filing as Money serves it (`GET /money/state-filings/:year`). */
export interface CoverFiling {
  kind: string;
  license: string | null;
  dueWords: string;
  lines: { no: string; cents: number | null; text?: string | null }[];
  /** Bingo only: the sessions played, a festival day each — `3 · Fri · Sat · Sun`. */
  sessions?: { count: number; days: string[] } | null;
}

export interface GamingCoversInput {
  raffle: CoverFiling | null;
  bingo: CoverFiling | null;
  /** The raffle books: tickets printed and sold; null when Marketing did not answer. */
  tickets: { printed: number; sold: number } | null;
  /** The last drawing, as printed (`Sep 12, 2026 · 9:30 PM`); null when none is recorded. */
  lastDrawing: string | null;
}

const thousands = (n: number): string => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
const cents = (f: CoverFiling | null, no: string): PaperCell =>
  f?.lines.find((l) => l.no === no)?.cents ?? NO_DATA;
/** The cover's address line; Money says the due date in words, or nothing when it has none. */
const to = (f: CoverFiling | null): string => {
  const due = f?.dueWords ?? '';
  return `To the Michigan Lottery · Charitable Gaming Division · license ${f?.license ?? NO_DATA} · due ${due === '' ? NO_DATA : due}`;
};

export function gamingCovers(i: GamingCoversInput): PaperPage[] {
  const t = i.tickets;
  return [
    {
      title: 'Raffle statement · cover',
      sub: to(i.raffle),
      blocks: [
        {
          head: '',
          cols: '1fr 140px',
          rows: [
            [
              'Tickets printed · sold · unsold',
              t
                ? `${thousands(t.printed)} · ${thousands(t.sold)} · ${thousands(t.printed - t.sold)}`
                : NO_DATA,
            ],
            ['Gross receipts', cents(i.raffle, '1')],
            ['Prizes paid', cents(i.raffle, '7')],
            ['Expenses', cents(i.raffle, '13')],
            ['Net proceeds', cents(i.raffle, 'Net')],
            ['Last drawing', i.lastDrawing ?? NO_DATA],
          ],
        },
      ],
      sign: ['Chairperson', 'Finance director'],
      foot: 'Raffle statement cover · figures from Money · the state’s form follows',
    },
    {
      title: 'Bingo statement · cover',
      sub: to(i.bingo),
      blocks: [
        {
          head: '',
          cols: '1fr 140px',
          rows: [
            // The days the count room counted bingo, one session each (M19).
            [
              'Sessions',
              i.bingo?.sessions
                ? [String(i.bingo.sessions.count), ...i.bingo.sessions.days].join(' · ')
                : NO_DATA,
            ],
            ['Gross receipts', cents(i.bingo, '3')],
            ['Prizes paid', cents(i.bingo, '4')],
            ['Expenses', cents(i.bingo, '11')],
            ['Net proceeds', cents(i.bingo, 'Net')],
          ],
        },
      ],
      sign: ['Chairperson', 'Finance director'],
      foot: 'Bingo statement cover',
    },
  ];
}
